import { z } from "zod";
import { APP_TIME_ZONE } from "./locale";

const optionalString = z
  .string()
  .trim()
  .transform((value) => (value === "" ? undefined : value))
  .optional();

const commaList = z
  .string()
  .trim()
  .transform((value) =>
    value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean),
  );

export const serverEnvSchema = z
  .object({
    NODE_ENV: z.enum(["development", "test", "production"]).default("development"),
    PORT: z.coerce.number().int().positive().default(4100),
    APP_URL: z.url(),
    APP_TIME_ZONE: z.string().default(APP_TIME_ZONE),
    LOG_LEVEL: z
      .enum(["fatal", "error", "warn", "info", "debug", "trace", "silent"])
      .default("info"),

    DATABASE_URL: z.url(),

    BETTER_AUTH_SECRET: z.string().min(32),
    BETTER_AUTH_URL: z.url(),

    MAIL_TRANSPORT: z.enum(["resend", "mailpit", "log"]).default("log"),
    MAILPIT_URL: optionalString.pipe(z.url().optional()),
    EMAIL_FROM_ADDRESS: z.email(),
    EMAIL_FROM_NAME: z.string().min(1).default("Purlink"),
    RESEND_API_KEY: optionalString,
    RESEND_WEBHOOK_SECRET: optionalString,
    PUBLIC_ASSET_BASE_URL: z.url(),

    TURNSTILE_SECRET_KEY: z.string().min(1),
    TURNSTILE_ALLOWED_HOSTNAMES: commaList.pipe(z.array(z.string()).min(1)),

    /** Meta Conversions API: off queues nothing, dry_run logs a redacted payload, live sends. */
    META_CAPI_MODE: z.enum(["off", "dry_run", "live"]).default("off"),
    META_CAPI_DATASET_ID: optionalString.pipe(
      z.string().regex(/^\d+$/, "Must be the numeric dataset ID").optional(),
    ),
    META_CAPI_ACCESS_TOKEN: optionalString,
    META_GRAPH_API_VERSION: z
      .string()
      .trim()
      .regex(/^v\d+\.\d+$/, "Must look like v26.0")
      .default("v26.0"),
    META_CAPI_TEST_EVENT_CODE: optionalString,
    META_CAPI_EVENT_SOURCE_URL: optionalString.pipe(z.url().optional()),
    /** marketing: only enquiries with the marketing box ticked. all: every enquiry (privacy notice names Meta). */
    META_CAPI_CONSENT: z.enum(["marketing", "all"]).default("all"),

    /** Reps new form leads are assigned to in turn, in this order. Empty turns it off. */
    LEAD_ROUND_ROBIN_EMAILS: commaList
      .pipe(z.array(z.email().transform((email) => email.toLowerCase())))
      .default([]),

    /** Where the in-app documents' Markdown lives. Defaults to the repo's docs/. */
    DOCS_DIR: optionalString,
  })
  .superRefine((env, ctx) => {
    if (env.MAIL_TRANSPORT === "resend" && !env.RESEND_API_KEY) {
      ctx.addIssue({
        code: "custom",
        path: ["RESEND_API_KEY"],
        message: "RESEND_API_KEY is required when MAIL_TRANSPORT=resend",
      });
    }
    if (env.MAIL_TRANSPORT === "mailpit" && !env.MAILPIT_URL) {
      ctx.addIssue({
        code: "custom",
        path: ["MAILPIT_URL"],
        message: "MAILPIT_URL is required when MAIL_TRANSPORT=mailpit",
      });
    }
    if (env.META_CAPI_MODE === "live") {
      for (const key of ["META_CAPI_DATASET_ID", "META_CAPI_ACCESS_TOKEN"] as const) {
        if (!env[key]) {
          ctx.addIssue({
            code: "custom",
            path: [key],
            message: `${key} is required when META_CAPI_MODE=live`,
          });
        }
      }
    }
    if (env.NODE_ENV === "production") {
      if (env.MAIL_TRANSPORT !== "resend") {
        ctx.addIssue({
          code: "custom",
          path: ["MAIL_TRANSPORT"],
          message: "MAIL_TRANSPORT must be resend in production",
        });
      }
      if (/^[123]x0+AA$/.test(env.TURNSTILE_SECRET_KEY)) {
        ctx.addIssue({
          code: "custom",
          path: ["TURNSTILE_SECRET_KEY"],
          message: "Cloudflare's Turnstile test secret can't be used in production",
        });
      }
      if (env.META_CAPI_EVENT_SOURCE_URL && !env.META_CAPI_EVENT_SOURCE_URL.startsWith("https:")) {
        ctx.addIssue({
          code: "custom",
          path: ["META_CAPI_EVENT_SOURCE_URL"],
          message: "META_CAPI_EVENT_SOURCE_URL must use https in production",
        });
      }
    }
  });

export type ServerEnv = z.infer<typeof serverEnvSchema>;

export const clientEnvSchema = z.object({
  VITE_TURNSTILE_SITE_KEY: z.string().min(1),
  /** GA4 measurement ID. Inlined at build time; empty turns analytics off. */
  VITE_GA_MEASUREMENT_ID: optionalString.pipe(
    z
      .string()
      .regex(/^G-[A-Z0-9]+$/, "must look like G-XXXXXXXXXX")
      .optional(),
  ),
  /** Meta Pixel ID (the CAPI dataset ID). Inlined at build time; empty turns the Pixel off. */
  VITE_META_PIXEL_ID: optionalString.pipe(
    z.string().regex(/^\d+$/, "must be the numeric Pixel (dataset) ID").optional(),
  ),
});

export type ClientEnv = z.infer<typeof clientEnvSchema>;

/** Formats Zod issues as one readable line per invalid variable. */
export function formatEnvErrors(error: z.ZodError): string {
  return error.issues
    .map((issue) => `  - ${issue.path.join(".") || "(root)"}: ${issue.message}`)
    .join("\n");
}
